import { Children, useId, useState, type FocusEvent, type MouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { resolveStorefront, StorefrontMark } from '@/components/warehouse/storefronts'
import type { StorefrontPrice, StorefrontReadiness } from '@/lib/api/schemas/inventory.schema'
import type { MarketplaceBadge } from '@/lib/warehouse/projection'
import { formatRubleAmount } from '@/lib/market-money'
import { connectionLabels, presenceLabels, unavailableReasonText, warehouseDateTime } from '@/lib/warehouse/labels'

/**
 * A value the payload does not carry is never blank and never zero: its visible
 * value is always a dash. Reasons belong to metadata, not the value position;
 * keep their code in `data-reason` and their Russian explanation in `title`.
 * Use `message` when the server supplied the explanation verbatim.
 */
export function ValueOrReason({ value, reason, suffix, message }: { value: string | number | null; reason: string | null; suffix?: string; message?: string | null }) {
  if (value === null || value === '') {
    const explanation = message ?? (reason ? unavailableReasonText(reason) : null)
    return (
      <span className="text-ink-muted" data-unavailable="true" data-reason={reason ?? undefined} title={explanation ?? undefined}>
        —
      </span>
    )
  }
  return <span className="font-medium text-ink">{value}{suffix ? ` ${suffix}` : ''}</span>
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-ink-muted">{label}</dt>
      <dd className="mt-1 break-words text-xs">{children}</dd>
    </div>
  )
}

const presenceTone: Record<string, string> = {
  present: 'border-st-delivered/40 bg-st-delivered/10 text-st-delivered',
  absent: 'border-rule bg-bone/60 text-ink-2',
  unknown: 'border-gold/40 bg-gold/5 text-gold-deep',
}

export interface MarketplaceBadgeDetail {
  providerCode: string
  price: StorefrontPrice | null
  readiness: StorefrontReadiness | null
  /** Replaces the default «Цена: …» part, e.g. with the Yandex Market price pair. */
  priceText?: string
}

const listingStateLabels: Record<string, string> = {
  live: 'На витрине',
  hidden: 'Скрыт',
  awaiting_review: 'На модерации',
  submitted: 'На модерации',
  rejected: 'Отклонён',
}

export function compactPrice(price: StorefrontPrice | null): string {
  if (price === null || price.price === null) {
    const explanation = price?.unavailableReason ? unavailableReasonText(price.unavailableReason) : null
    return explanation ? `— (${explanation})` : '—'
  }
  const actual = formatRubleAmount(price.price)
  return price.oldPrice === null
    ? actual
    : `${actual} · зачёркнутая ${formatRubleAmount(price.oldPrice)}`
}

function compactListingState(price: StorefrontPrice | null): string {
  if (price === null) return 'Статус размещения не указан'
  if (
    price.placementState === 'unknown'
    || (price.placementState === undefined && price.connectionState === 'no_observation')
  ) return 'Статус размещения не указан'
  if (price.listingState !== null) return listingStateLabels[price.listingState] ?? 'Статус размещения не указан'
  return price.listed ? 'На витрине' : 'Не размещено'
}

function compactReadinessIssue(issue: StorefrontReadiness['issues'][number]): string {
  const looksLikeCode = /^[A-Z0-9]+(?:_[A-Z0-9]+)+$/.test(issue.label) || /^[a-z0-9]+(?:[._:-][a-z0-9]+)+$/.test(issue.label)
  return looksLikeCode ? unavailableReasonText(issue.code) : issue.label
}

function compactReadiness(readiness: StorefrontReadiness | null): string {
  if (readiness === null) return 'Проверка недоступна'
  if (readiness.issues.length > 0) return readiness.issues.map(compactReadinessIssue).join(', ')
  return readiness.ready ? 'Готово' : 'Не готово'
}

type OpenBadgeTooltip = { key: string; text: string; x: number; y: number }

const TOOLTIP_WIDTH = 320

/** The storefront detail as a visible tooltip, one fact per line, anchored beside the mark. */
function BadgeTooltip({ tooltip, id }: { tooltip: OpenBadgeTooltip; id: string }) {
  const left = Math.max(8, Math.min(tooltip.x + 12, window.innerWidth - TOOLTIP_WIDTH - 8))
  return createPortal(
    <div
      role="tooltip"
      id={id}
      data-storefront-tooltip={tooltip.key}
      className="pointer-events-none fixed z-50 w-max max-w-[320px] rounded-md bg-onyx px-2.5 py-2 text-xs text-ivory shadow-lg"
      style={{ left: `${left}px`, top: `${tooltip.y + 12}px` }}
    >
      {tooltip.text.split(' · ').map((line, index) => <span key={`${index}:${line}`} className="block">{line}</span>)}
    </div>,
    document.body,
  )
}

export function MarketplaceBadgeList({ badges, compact = false, details, connectionOnly = false }: {
  badges: readonly MarketplaceBadge[]
  compact?: boolean
  details?: readonly MarketplaceBadgeDetail[]
  connectionOnly?: boolean
}) {
  const [tooltip, setTooltip] = useState<OpenBadgeTooltip | null>(null)
  const tooltipId = `storefront-tooltip-${useId().replaceAll(':', '')}`
  const close = (key: string) => setTooltip((current) => (current?.key === key ? null : current))
  return (
    <ul className={`flex gap-1.5 ${compact ? 'min-w-max flex-nowrap' : 'flex-wrap'}`} aria-label="Площадки">
      {badges.map((badge) => {
        const notConnected = badge.connectionState === 'not_connected'
        const storefrontState = notConnected ? 'off' : badge.presence
        const stateText = connectionOnly
          ? connectionLabels[badge.connectionState]
          : notConnected ? connectionLabels.not_connected : presenceLabels[badge.presence]
        const storefront = resolveStorefront(badge.providerCode)
        const detail = details?.find((candidate) => candidate.providerCode === badge.providerCode) ?? null
        const markOpacity = connectionOnly
          ? (badge.connectionState === 'connected' ? 1 : 0.55)
          : detail === null
            ? (notConnected || badge.presence === 'absent' ? 0.55 : 1)
          : detail.price?.listed === true && badge.connectionState === 'connected' && badge.presence === 'present' && detail.readiness?.ready === true ? 1 : 0.55
        const compactLabel = detail === null
          ? null
          : `${badge.displayName}: ${compactListingState(detail.price)} · ${detail.priceText ?? `Цена: ${compactPrice(detail.price)}`} · Готовность: ${compactReadiness(detail.readiness)}`
        const key = `${badge.providerCode}:${badge.accountCode ?? 'none'}`
        const tooltipHandlers = compactLabel === null ? {} : {
          tabIndex: 0,
          'aria-describedby': tooltip?.key === key ? tooltipId : undefined,
          onMouseEnter: (event: MouseEvent<HTMLSpanElement>) => setTooltip({ key, text: compactLabel, x: event.clientX, y: event.clientY }),
          onMouseLeave: () => close(key),
          onFocus: (event: FocusEvent<HTMLSpanElement>) => {
            const rect = event.currentTarget.getBoundingClientRect()
            setTooltip({ key, text: compactLabel, x: rect.right, y: rect.bottom })
          },
          onBlur: () => close(key),
        }
        return (
          <li key={key}>
            <span
              {...tooltipHandlers}
              aria-label={compactLabel ?? `${badge.displayName}: ${stateText}`}
              data-storefront-state={storefrontState}
              className={`inline-flex size-6 min-w-max items-center justify-center whitespace-nowrap rounded border ${notConnected ? 'border-dashed border-rule bg-transparent text-ink-muted opacity-60' : presenceTone[badge.presence]}`}
              data-storefront={storefront.kind === 'known' ? badge.providerCode : 'unknown'}
              data-provider-code={storefront.kind === 'unknown' ? storefront.providerCode : undefined}
              data-storefront-connection={connectionOnly ? badge.connectionState : undefined}
              title={compactLabel === null ? `${badge.displayName} · ${stateText}${badge.observedAt ? ` · ${warehouseDateTime(badge.observedAt)}` : ''}${storefront.kind === 'unknown' ? ' · Неизвестная площадка' : ''}` : undefined}
            >
              <StorefrontMark providerCode={badge.providerCode} opacity={markOpacity} />
            </span>
            {compact && !connectionOnly ? null : (
              <span className="sr-only" data-reason={badge.unavailableReason ?? undefined}>
                {badge.displayName}: {stateText}{badge.unavailableReason ? `, причина ${unavailableReasonText(badge.unavailableReason)}` : ''}
              </span>
            )}
          </li>
        )
      })}
      {tooltip !== null ? <BadgeTooltip tooltip={tooltip} id={tooltipId} /> : null}
    </ul>
  )
}

export function SectionCard({ title, subtitle, children, action, ariaLabel }: {
  title: string
  subtitle?: string
  children: ReactNode
  action?: ReactNode
  ariaLabel?: string
}) {
  return (
    <section className="rounded-lg border border-rule bg-paper" aria-label={ariaLabel ?? title}>
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-rule px-4 py-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-ink">{title}</h3>
          {subtitle ? <p className="mt-0.5 text-[11px] text-ink-muted">{subtitle}</p> : null}
        </div>
        {action}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  )
}

/** A bounded list whose disclosure count is the server's population total. */
export function BudgetedList({ children, budget, total, noun = 'строк', className = 'space-y-2' }: {
  children: ReactNode
  budget: number
  total: number
  noun?: string
  className?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const rows = Children.toArray(children)
  const visible = expanded ? rows : rows.slice(0, budget)
  return (
    <div data-budget={budget} data-population-total={total}>
      <ul className={className}>{visible}</ul>
      {total > budget ? (
        <button type="button" onClick={() => setExpanded((value) => !value)} className="mt-3 text-[11px] text-gold-deep underline">
          {expanded ? 'Свернуть' : `Показать все ${total} ${noun}`}
        </button>
      ) : null}
    </div>
  )
}

/** Fixed-width summary facts; callers cannot place an unbounded list here. */
export function SummaryStrip({ facts }: { facts: readonly { label: string; value: ReactNode; tone?: 'warn' }[] }) {
  return (
    <dl className="grid grid-cols-2 divide-x divide-rule overflow-hidden rounded-lg border border-rule bg-paper sm:grid-cols-4 lg:grid-cols-8">
      {facts.map((fact) => (
        <div key={fact.label} className="min-w-0 px-3 py-2.5">
          <dt className="truncate text-[10px] text-ink-muted">{fact.label}</dt>
          <dd className={`mt-1 truncate text-xs font-semibold ${fact.tone === 'warn' ? 'text-st-unpaid' : 'text-ink'}`}>{fact.value}</dd>
        </div>
      ))}
    </dl>
  )
}

export function SectionIndex({ sections }: { sections: readonly { id: string; label: string; count?: number; tone?: 'warn' }[] }) {
  return (
    <nav aria-label="Разделы карточки" className="sticky top-4 rounded-lg border border-rule bg-paper p-2">
      <ul className="space-y-0.5 text-[11px]">
        {sections.map((section) => (
          <li key={section.id}>
            <a href={`#${section.id}`} className={`flex justify-between gap-2 rounded px-2 py-1.5 hover:bg-bone/60 ${section.tone === 'warn' ? 'text-st-unpaid' : 'text-ink-2'}`}>
              <span>{section.label}</span>{section.count === undefined ? null : <span className="tabular-nums text-ink-muted">{section.count}</span>}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
